import {
  AgentDecision,
  ClaimType,
  ClaimCategory,
  ClaimSubtype,
  ClaimAutomation,
  ClaimAutomationFeedback,
  ClaimPauseReason,
  ClaimPauseSource,
  type Claim,
} from '@monolog/shared/types/database/claims-svc';
import type {
  ClaimEscalationPeek,
  ClaimSentimentSortKey,
  ClaimSentimentSortOrder,
  MessageSentiment,
  OpenRecommendationList,
  ReactionLane,
  ReactionLaneSource,
  ReactionRootCause,
  SentimentSource,
  ClaimAutomationRunView,
} from '@monolog/shared/types';
import { tap } from 'rxjs';

import { useSeasonalCelebrationsStore } from '~/store/seasonal-celebrations';

import GatewayBase from './gateway-base';
import type { Capabilities } from './merchant.rest.service';
import type { ReviewSummary, ReviewUserProfile } from '@monolog/shared/types/api/reviews.types';
import type { FraudRiskLevel } from '@monolog/shared/types/automation.types';
import type { FraudIndicator } from '@monolog/shared/types/fraud-check.types';
import type { Note, NoteMedia } from '@monolog/shared/types/database/metadata-svc';
import { CreateClaimDto, ResolveClaimDto, ValidateOfflineDto } from '~/support/services/types/claim.types';
import { StoreCreditResponse } from '~/support/types/store-credit.types';
import { IssueGalleryCounts } from '~/support/services/types/issue-gallery.types';

/** Pause row as the API returns it. `dueAt` is the watchdog's next rung, `nudgedAt` marks overdue. */
/** The patterns dashboard's window, optionally narrowed to one store. */
export interface FailurePatternParams {
  startDate?: string;
  endDate?: string;
  storeId?: string;
}

/** One tier-1 pile of automation failures, as ranked by the patterns dashboard. */
export type FailurePattern = {
  groupKey: string;
  resolutionType: string;
  status: string;
  shape: string;
  author: string;
  findings: number;
  /** Net USD, signed — over-delivery offsets under-delivery. */
  shortfallUsd: number;
  /** Total USD that moved wrongly, ignoring direction. What ranking and the charts use. */
  magnitudeUsd: number;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  /** Null while nothing has explained this pile yet, which is itself the interesting state. */
  cause: { id: string; label: string; source: string } | null;
  previousFindings: number;
  /** Order ids, not claim ids: the claim view route is keyed on the order. */
  exampleOrderIds: string[];
  /** Distinct causes written on this pile's failures. Empty until someone diagnoses it. */
  causes: string[];
  /** Deliveries of this resolution type by this author in the window — the pile's denominator. */
  deliveries: number;
  /** Percentage. Null when nothing comparable was delivered, which is not a zero rate. */
  failureRate: number | null;
};

export type StoreFailureRow = {
  storeId: string;
  findings: number;
  shortfallUsd: number;
  magnitudeUsd: number;
  deliveries: number;
  failureRate: number | null;
  lastSeenAt: string | null;
};

/** The day a code version was first seen producing a finding — a deploy-boundary proxy. */
export type PatternVersionBoundary = {
  commitHash: string;
  day: string;
};

export type FailurePatternDay = {
  day: string;
  shape: string;
  findings: number;
  shortfallUsd: number | null;
};

export type ClaimSentimentRow = {
  id: string;
  claimId: string;
  storeId: string;
  source: SentimentSource;
  automationId: string | null;
  sentiment: MessageSentiment;
  needsFollowUp: boolean | null;
  confidence: number | null;
  summary: string | null;
  lane: ReactionLane | null;
  laneSource: ReactionLaneSource | null;
  laneConfidence: number | null;
  laneReason: string | null;
  laneRootCause: ReactionRootCause | null;
  createdAt: string;
  claim: { orderId: string; sourceOrderNumber: string | null; claimType: string | null };
  message: { messageContent: string } | null;
  review: { rating: number; comments: string | null } | null;
};

export type ClaimSentimentListParams = {
  startDate?: string;
  endDate?: string;
  filters?: string;
  sort?: ClaimSentimentSortKey;
  order?: ClaimSentimentSortOrder;
  page?: number;
  limit?: number;
};

export type SentimentLaneDay = {
  day: string;
  lane: ReactionLane | null;
  count: number;
};

export type StoreSentimentRow = {
  storeId: string;
  total: number;
  /** Reactions on AI resolutions, the base for the store's negative-on-AI share. */
  onAi: number;
  negative: number;
  negativeOnAi: number;
  aiFault: number;
  lastSeenAt: string;
};

export type ClaimSentimentSummary = {
  total: number;
  bySentiment: Record<MessageSentiment, number>;
  negativeOnAi: number;
  aiFault: number;
  unclassified: number;
  byLane: Record<ReactionLane, number>;
  laneByDay: SentimentLaneDay[];
  stores: StoreSentimentRow[];
};

export type AutomationPauseRecord = {
  id: string;
  pausedAt: string;
  pausedBy: string;
  /** Resolved server-side on GET only; null for the AI's own brand holds or when identity can't be reached. */
  pausedByName?: string | null;
  reason: string | null;
  reasonCode: ClaimPauseReason | null;
  dueAt: string;
  nudgedAt: string | null;
  extendedAt: string | null;
  extendCount: number;
  source: ClaimPauseSource;
};

/** Mirrors `FraudCheckSummary` in @monolog/shared/types (ENG-6341 backend); swap to it once that type is on main. */
export type ClaimFraudCheck = {
  version: string;
  score: number;
  riskLevel: FraudRiskLevel.LOW | FraudRiskLevel.MEDIUM | FraudRiskLevel.HIGH;
  indicators: FraudIndicator[];
  computedAt: string;
};

/**
 * Server-side enrichment layered on top of the auto-generated `ClaimAutomation`
 * type by `automation.service.ts:getClaimAutomations`. Feedback rows and
 * resolution records pick up a `userName` resolved via the identity client,
 * and the new `decidedByUserName` field mirrors `decidedByUserId`. Encoding
 * the shape here means call sites can read these fields without casting.
 */
export type EnrichedClaimAutomation = Omit<ClaimAutomationRunView, 'feedback'> & {
  decidedByUserName: string | null;
  feedback: (ClaimAutomationFeedback & { userName: string | null })[];
  resolutionRecords: { id: string; userId: string; createdAt: string; userName: string | null }[];
};

/** Review note from the claims-svc proxy — metadata Note enriched with the author profile. */
export type ReviewNoteResponse = Note & { user: ReviewUserProfile | null; media: NoteMedia[] };

export type StoreStatsSortKey =
  | 'name'
  | 'adjustedAccuracy'
  | 'grade'
  | 'combinedAccuracy'
  | 'avgCostPerClaim'
  | 'totalCost'
  | 'claims'
  | 'csat'
  | 'avgRunSeconds';

/** Share of graded AI resolutions without each kind of miss, from the customer's own sentiment. */
export interface SentimentGradeBreakdown {
  /** AI resolutions with at least one readable customer reply or review: the headline and decision count. */
  graded: number;
  /** Replies plus resolutions whose message amount was checked: the message count. */
  messageGraded: number;
  /** AI resolutions with a settled delivery: the execution denominator. */
  executionGraded: number;
  decision: number | null;
  message: number | null;
  execution: number | null;
  decisionMisses: number;
  messageMisses: number;
  /** The misses behind `messageMisses` and `executionMisses`, by where they were found; one miss can be in both. */
  messageReplyMisses: number;
  messageTrackedMisses: number;
  executionMisses: number;
  executionReplyMisses: number;
  executionTrackedMisses: number;
}

export type StoreAutomationStatus = 'DISABLED' | 'OFF' | 'FULL_MANUAL' | 'FULL_AUTO' | 'MIXED';

/** Merged row from claims-svc store-stats: the merchant store hydrated with period aggregates. */
/** Why a gateway model can't be set on a store; mirrors the claims-svc catalog. */
export enum GatewayModelUnavailableReason {
  NO_KEY = 'NO_KEY',
  NOT_ON_OUR_HOST = 'NOT_ON_OUR_HOST',
  FIXED_DIMENSIONS = 'FIXED_DIMENSIONS',
}

/** An AI feature on the AI Models page and the model it runs, by its company/model id; null while on its default. */
export interface FeatureModel {
  feature: string;
  label: string;
  modelLabel: string | null;
  model: string | null;
  gatewayModel: string | null;
  directModel: string | null;
  defaultGatewayModel: string | null;
  /** False for a feature with no model picker; `reason` says why. */
  pickable: boolean;
  reason: string | null;
  /** Whether the feature's gateway calls can fall back; false for one limited to some models. */
  takesFallbacks: boolean;
  /** Picked per store (claim automation): its model here is the default a store with no model of its own runs. */
  perStore: boolean;
  /** The models its gateway calls try, in order, when its model errors. */
  fallbacks: ChainModel[];
}

/** A model in a model chain, by its company/model id; null fields when none is set. */
export interface ChainModel {
  model: string | null;
  gatewayModel: string | null;
  directModel: string | null;
  defaultGatewayModel: string | null;
  label: string | null;
  /** The OpenRouter provider a fallback is pinned to; null runs it on its company's own host. */
  provider?: string | null;
}

/** A model from the LLM gateway's catalog; only `selectable` ones can be set on a store. */
export interface GatewayModel {
  id: string;
  name: string;
  contextLength: number | null;
  readsImages: boolean;
  /** What we pay per million tokens: the price on the host we run the model on. Null when we can't run it. */
  pricing: { input: number | null; output: number | null; cacheRead: number | null };
  selectable: boolean;
  unavailableReason: GatewayModelUnavailableReason | null;
  /** The hosts a fallback can be pinned to: its own and each zero-retention one, cheapest first. */
  hosts?: ModelProviderOption[];
}

export interface StoreAutomationStatsItem {
  id: string;
  name: string;
  slug: string;
  storeDomain: string;
  platformId: string;
  storeSettings: { storeLogoUrl?: string | null } | null;
  capabilities: Capabilities | null;
  claimAutomationSettings: {
    model: string | null;
    gatewayModel?: string | null;
    aiModelId?: string | null;
    /** The OpenRouter provider the store's ai_model row is pinned to; null when unpinned. */
    aiModelProvider?: string | null;
    automationCapabilities: Record<string, boolean> | null;
  } | null;
  /** Whether the store's runs go through the LLM gateway, so its gateway model (if set) is the one that runs. */
  gatewayEnabled?: boolean;
  /** Whether the store's runs go through the gateway right now: its flag is on and the gateway can serve its model. */
  runsOnGateway?: boolean;
  /** The gateway catalog entry the store's own model maps to, if any. */
  defaultGatewayModel?: string | null;
  status: StoreAutomationStatus;
  /** The ai_model ids that actually ran for this store in the date range, busiest first. */
  modelsUsed: string[];
  /** Each of `modelsUsed`'s display names, in the same order. */
  modelsUsedNames?: string[];
  runs: number;
  claims: number;
  accepted: number;
  autoExecuted: number;
  rejected: number;
  discardedFeedback: number;
  accuracy: number | null;
  adjustedAccuracy: number | null;
  /** Decision misses count fully and message misses half; execution misses stay out of the blend. */
  grade: number | null;
  gradeBreakdown: SentimentGradeBreakdown;
  /** Adj. accuracy with the customer-reported misses behind `grade` counted as well. */
  combinedAccuracy: number | null;
  avgCostPerClaim: number | null;
  totalCost: number | null;
  csat: number | null;
  reviewCount: number;
  avgRunSeconds: number | null;
}

/** One UTC day of token usage, run duration, and throughput for a single model. */
export interface ModelTokenDay {
  date: string;
  runs: number;
  /** Distinct claims that ran this day — summing days double-counts a claim that ran on several. */
  claims: number;
  /** Stamped run costs plus current-rate pricing for any run that was never stamped. */
  cost: number;
  inputTokens: number;
  outputTokens: number;
  cacheCreationInputTokens: number;
  cacheReadInputTokens: number;
  /** Enqueue-to-decision seconds, summed over completed runs that carry a finishedAt stamp. */
  durationSecondsSum: number;
  /** Completed runs behind durationSecondsSum — the denominator for any re-bucketed average. */
  durationRuns: number;
  /** Agent-runner loops, summed over the runs that carry the iterations stamp. */
  iterationsSum: number;
  /** Runs behind iterationsSum — runs from before the column existed are excluded. */
  iterationRuns: number;
}

/**
 * One cohort day: claims whose FIRST run on this model landed that day, with their full
 * lifetime cost and run count. Each claim appears in exactly one cohort day, so unlike
 * `ModelTokenDay.claims` these sum correctly when days roll up into weeks or months.
 */
export interface ModelCohortDay {
  date: string;
  claims: number;
  runs: number;
  cost: number;
}

/** Cross-model totals for the models dashboard's "All Models" reference row. */
export type ModelStatsTotals = Omit<StoreModelSummary, 'model' | 'modelName' | 'company'>;

/** Of a row's calls and cost, the part routed through the OpenRouter gateway; the rest went direct. */
export interface AiSpendGatewaySplit {
  gatewayCalls: number;
  gatewayCost: number;
}

export interface AiSpendTotals extends AiSpendGatewaySplit {
  calls: number;
  failedCalls: number;
  cost: number;
  inputTokens: number;
  outputTokens: number;
  unpricedCalls: number;
}

export interface AiSpendByFeature {
  feature: string;
  calls: number;
  failedCalls: number;
  cost: number;
}

/** A store's claim automation model, by its company/model id; nulls when unset. */
export interface StoreModel {
  model: string | null;
  /** Set when the model only runs on the gateway. */
  gatewayModel: string | null;
  /** The gateway catalog entry a model we call directly maps to. */
  defaultGatewayModel: string | null;
  label: string | null;
  supportsThinking: boolean;
  /** Returned when the model is set, so the store's provider options can follow it. */
  aiModelId?: string;
}

export interface AiSpendByModel extends AiSpendGatewaySplit {
  /** The ai_model id, or 'unknown' for calls recorded without one. */
  model: string;
  modelName: string;
  vendor: string;
  calls: number;
  cost: number;
  avgCost: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
}

export interface AiSpendBreakdown {
  model: string;
  modelName: string;
  feature: string;
  calls: number;
  failedCalls: number;
  cost: number;
  inputTokens: number;
  outputTokens: number;
}

export interface AiSpendByDay {
  date: string;
  feature: string;
  cost: number;
}

export interface AiSpendStore {
  storeId: string;
  name: string | null;
  calls: number;
  cost: number;
  avgCost: number;
  byFeature: Record<string, { cost: number; calls: number; failedCalls: number }>;
}

/** Fraud detection is Fraud Signals plus the fraud prompt; flagged-run spend is context, not part of it. */
export interface AiSpendFraud {
  fraudSignalsCost: number;
  /** Null until a call in the range records the fraud prompt's cost. */
  fraudPromptCost: number | null;
  fraudPromptCalls: number;
  fraudPromptRecordedFrom: string | null;
  /** Automation calls that read a shown fraud check; none means the fraud prompt really cost nothing. */
  fraudCheckedCalls: number;
  fraudCheckedFrom: string | null;
  fraudDetectionCost: number;
  flaggedRunsCost: number;
  flaggedRuns: number;
  flaggedClaims: number;
}

export interface AiSpendResponse {
  startDate: string;
  endDate: string;
  totals: AiSpendTotals;
  /** Absent from a server that predates the fraud breakdown. */
  fraud?: AiSpendFraud;
  byFeature: AiSpendByFeature[];
  byModel: AiSpendByModel[];
  byDay: AiSpendByDay[];
  breakdown: AiSpendBreakdown[];
  stores: AiSpendStore[];
}

/** A zero-retention OpenRouter provider (or the model's own host) a model's calls can be pinned to. */
export interface ModelProviderOption {
  /** The routing tag a call is pinned to, e.g. digitalocean. */
  provider: string;
  name: string;
  quantization: string | null;
  /** USD per million tokens; null when the gateway doesn't list it. */
  pricing: { input: number | null; output: number | null; cacheRead: number | null };
  uptime: number | null;
  /** Runs on our own vendor key; every other provider bills gateway credits. */
  ownHost: boolean;
}

export interface ModelProviders {
  providers: ModelProviderOption[];
}

/** Per-model rollup of the period's runs, attributed to the model that executed each run. */
export interface StoreModelSummary {
  /** The ai_model id. */
  model: string;
  modelName: string;
  company: string | null;
  /** The OpenRouter provider these runs were pinned to; null for unpinned runs. */
  provider: string | null;
  /** The distinct provider picks of the stores currently on the model; null for an unpinned store. */
  storeProviders?: Array<string | null>;
  stores: number;
  currentStores: number;
  runs: number;
  claims: number;
  completedRuns: number;
  failedRuns: number;
  supersededRuns: number;
  stoppedRuns: number;
  completedCost: number;
  failedCost: number;
  supersededCost: number;
  stoppedCost: number;
  avgIterations: number | null;
  avgRunSeconds: number | null;
  totalCost: number | null;
  avgCostPerClaim: number | null;
  inputTokens: number;
  outputTokens: number;
  cacheReadInputTokens: number;
  accuracy: number | null;
  adjustedAccuracy: number | null;
  grade: number | null;
  gradeBreakdown: SentimentGradeBreakdown;
  combinedAccuracy: number | null;
  csat: number | null;
  reviewCount: number;
}

export interface ReviewNoteMediaInput {
  fileKey: string;
  filename?: string;
  caption?: string;
  fileType?: string;
  url?: string;
  downloadLink?: string;
}

export interface CreateReviewNoteInput {
  content: string;
  threadId?: string;
  media?: ReviewNoteMediaInput[];
}

export type TrainingIssueStatus = 'OPEN' | 'INVESTIGATING' | 'FIX_IN_PROGRESS' | 'FIX_MERGED' | 'RESOLVED' | 'CLOSED';

export type TrainingIssueCloseReason = 'AGENT_WRONG' | 'SOP_CHANGED' | 'CODE_FIX' | 'WONT_FIX';

export type TrainingIssueCategory =
  | 'WRONG_DECISION'
  | 'WRONG_AMOUNT'
  | 'MESSAGE'
  | 'NEEDED_GUIDANCE'
  | 'REJECTED_UNRESOLVED'
  | 'REJECTED_SAME_OUTCOME';

export type TrainingIssueMember = {
  feedbackId: string;
  feedbackType: string;
  message: string | null;
  reviewStatus: string;
  storeSpecific: boolean;
  createdAt: string;
  automationId: string;
  claimId: string;
  orderId: string | null;
  sourceOrderNumber: string | null;
  claimState: string | null;
  action: string | null;
  aiDecision: string | null;
  agentDecision: string | null;
  suggestedResolution: string | null;
  suggestedAmount: number | null;
  confidence: number | null;
  reason: string | null;
  commitHash: string | null;
  humanResolution: string | null;
  humanAmount: number | null;
  currency: string | null;
  afterFix: boolean;
};

export type TrainingIssueRef = {
  key: string;
  storeId: string;
  fingerprint: string;
  category: TrainingIssueCategory;
  feedbackCount: number;
  status: TrainingIssueStatus;
};

export type SimilarTrainingIssueRef = TrainingIssueRef & {
  claimType: string;
  aiOutcome: string;
  humanOutcome: string | null;
  score: number;
};

export type TrainingIssue = {
  key: string;
  storeId: string;
  fingerprint: string;
  category: TrainingIssueCategory;
  claimType: string;
  aiOutcome: string;
  humanOutcome: string | null;
  direction: 'OVER' | 'UNDER' | null;
  feedbackCount: number;
  claimCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
  status: TrainingIssueStatus;
  closeReason: TrainingIssueCloseReason | null;
  prUrl: string | null;
  note: string | null;
  mergedAt: string | null;
  resolvedAt: string | null;
  stateUpdatedAt: string | null;
  sinceFixCount: number;
  regressed: boolean;
  alsoAt: TrainingIssueRef[];
  similar: SimilarTrainingIssueRef[];
  members: TrainingIssueMember[];
};

export type FeedbackIssuesResponse = {
  issues: TrainingIssue[];
  categories: { category: TrainingIssueCategory; issues: number; feedback: number }[];
  totalFeedback: number;
  truncated: boolean;
};

export type UpdateTrainingIssueInput = {
  storeIds: string[];
  fingerprint: string;
  status: TrainingIssueStatus;
  closeReason?: TrainingIssueCloseReason;
  prUrl?: string;
  note?: string;
};

export type TrainingIssueSkipReason = 'ALREADY_RESOLVED' | 'ALREADY_CLOSED' | 'DIFFERENT_PR' | 'HAS_OWN_PR';

export type UpdateTrainingIssueResult = {
  updated: string[];
  skipped: { storeId: string; reason: TrainingIssueSkipReason }[];
};

export interface FeedbackTheme {
  id: string;
  name: string;
  count: number;
  scope: string;
  targetFile: string;
  rationale: string;
  automationIds: string[];
  examples: string[];
}

export default class ClaimsRestService extends GatewayBase {
  private claimsForOrder = (orderId: string) => {
    return this.client.get<any[]>(`/v1/claims/order/${orderId}`);
  };

  private getPoliciesByOrderId = (orderId: string) => {
    return this.client.get<any>(`/v1/policies/${orderId}`);
  };

  private createPolicy = (data: any) => {
    return this.client.post<any>('/v1/policies', data);
  };

  private removePolicy = (policyId: string, body: { platformId: string; sourceOrderId: string; storeId: string }) => {
    return this.client.put<any>(`/v1/policies/${policyId}/remove`, body);
  };

  get activities() {
    return {
      shopify: this.getShopifyActivities,
    };
  }

  get policies() {
    return {
      create: this.createPolicy,
      forOrder: this.getPoliciesByOrderId,
      remove: this.removePolicy,
    };
  }

  get claims() {
    return {
      assign: this.assignClaim,
      automation: {
        admin: {
          applyFeedback: this.applyFeedback,
          applyFeedbackItems: this.applyFeedbackItems,
          auditFeedback: this.auditFeedback,
          auditFeedbackItems: this.auditFeedbackItems,
          byClaim: this.listAutomationsByClaim,
          claimsList: this.listClaimsWithAutomations,
          approveRecommendations: this.approveOpenRecommendations,
          retryFailed: this.retryFailedAutomations,
          clusterFeedback: this.clusterFeedback,
          clusterFeedbackStatus: this.clusterFeedbackStatus,
          countFeedback: this.countAutomationFeedback,
          feedbackIssues: this.listFeedbackIssues,
          courseCorrectionStats: this.getCourseCorrectionStats,
          createTrainingPr: this.createTrainingPr,
          deleteFeedback: this.deleteFeedback,
          deleteFeedbackItems: this.deleteFeedbackItems,
          discardFeedback: this.discardFeedback,
          discardFeedbackItems: this.discardFeedbackItems,
          generateTraining: this.generateTraining,
          generateTrainingStatus: this.generateTrainingStatus,
          get: this.getAutomationById,
          list: this.listAutomationsAdmin,
          listFeedback: this.listAutomationFeedback,
          updateFeedbackIssue: this.updateFeedbackIssue,
          messageAuthor: this.messageFeedbackAuthor,
          aiSpend: this.getAiSpend,
          gatewayModels: this.getGatewayModels,
          embeddingModels: this.getEmbeddingModels,
          featureModels: this.getFeatureModels,
          setFeatureModel: this.setFeatureModel,
          resetFeatureModel: this.resetFeatureModel,
          setFeatureFallbacks: this.setFeatureFallbacks,
          directStoreModels: this.getDirectStoreModels,
          setStoreModel: this.setStoreModel,
          modelStats: this.getModelAutomationStats,
          modelProviders: this.getModelProviders,
          setModelProvider: this.setModelProvider,
          setStoreModelProvider: this.setStoreModelProvider,
          modelTokensByDate: this.getModelTokensByDate,
          openRecommendations: this.listOpenRecommendations,
          patterns: this.getFailurePatterns,
          patternsByDate: this.getFailurePatternsByDate,
          patternsVersions: this.getFailurePatternVersions,
          sentiment: this.listClaimSentiment,
          sentimentSummary: this.getClaimSentimentSummary,
          stats: this.getAutomationStats,
          storeStats: this.getStoreAutomationStats,
        },
        approveRecommendation: this.approveAutomationRecommendation,
        cancelScheduled: this.cancelScheduledExecution,
        execute: this.executeAutomation,
        extendPause: this.extendAutomationPause,
        getActive: this.getActiveAutomation,
        getEscalation: this.getClaimEscalation,
        getBrandVoice: this.getBrandVoice,
        getPause: this.getAutomationPause,
        getStatus: this.getAutomationStatus,
        getStoreModel: this.getStoreModel,
        pause: this.pauseAutomation,
        regenerate: this.regenerateAutomation,
        resume: this.resumeAutomation,
        stop: this.stopAutomation,
        submitFeedback: this.submitAutomationFeedback,
        submitGuidance: this.submitAutomationGuidance,
        update: this.updateAutomation,
      },
      counts: {
        forStores: this.getClaimCountsForStore,
        resolved: this.getResolvedCount,
      },
      create: this.createClaim,
      createInternal: this.createInternalClaim,
      createMessage: this.createClaimMessage,
      credits: {
        delete: this.deleteStoreCredit,
        export: this.exportCredits,
        list: {
          get: this.getStoreCredits,
        },
        usage: {
          get: this.getClaimCreditUsage,
        },
      },
      export: {
        forStore: this.exportClaims,
      },
      forOrder: this.claimsForOrder,
      forStores: this.claimsForStore,
      fraudCheck: this.getClaimFraudCheck,
      gallery: {
        counts: {
          forStores: this.claimGalleryCountsForStore,
        },
        forStores: this.claimGalleryForStores,
      },
      generateAcknowledgement: this.generateAcknowledgement,
      holdForBrand: this.holdForBrand,
      navigate: this.getClaimNavigation,
      notifyCustomer: this.notifyClaimCustomer,
      resetClaim: this.resetClaim,
      resolve: this.resolveClaim,
      search: this.searchClaims,
      subtypes: {
        create: this.createClaimSubtype,
        get: this.getClaimSubtypes,
        update: this.updateClaimSubtype,
      },
      updateClaimType: this.updateClaimType,
      updateState: this.updateClaimState,
      warranties: {
        counts: {
          forStores: this.getWarrantyCountsForStores,
        },
        export: this.exportWarranties,
        forStores: this.getWarranties,
        validate: this.validateOffline,
      },
    };
  }

  private createClaim = (sourceOrderId: string, body: CreateClaimDto) => {
    return this.client.post<any>(`/v1/claims/${sourceOrderId}`, body);
  };

  private createInternalClaim = (sourceOrderId: string, body: CreateClaimDto) => {
    return this.client.post<any>(`/v1/claims/${sourceOrderId}/valet`, body);
  };

  private notifyClaimCustomer = (claimId: string) => {
    return this.client.post<any>(`/v1/claims/notify/${claimId}/email`);
  };

  private generateAcknowledgement = (claimId: string) => {
    return this.client.post<{ success: boolean }>(`/v1/claims/${claimId}/acknowledgement/generate`);
  };

  private exportClaims = (body: unknown) => {
    return this.client.post<any>(`/v1/claims/export-claims`, body);
  };

  get reviews() {
    return {
      check: this.setReviewChecked,
      count: this.getReviewsCount,
      export: this.exportReviews,
      get: this.getReviews,
      notes: {
        create: this.createReviewNote,
        get: this.getReviewNotes,
        remove: this.deleteReviewNote,
      },
      summary: this.getReviewsSummary,
    };
  }
  private exportCredits = (body: unknown) => {
    return this.client.post<string>(`/v1/claims/credits/export`, body);
  };

  private deleteStoreCredit = (storeCreditId: string, storeId) => {
    return this.client.delete(`/v1/claims/credits/${storeCreditId}`, {
      params: { storeId },
    });
  };

  private getStoreCredits = (params?: {
    filters?: string;
    page?: number;
    limit?: number;
    createdAtMin?: Date;
    createdAtMax?: Date;
  }) => {
    return this.client.get<StoreCreditResponse>(`/v1/claims/credits/list`, { params });
  };

  private getClaimCountsForStore = (params?: { filters?: string; createdAtMin?: Date; createdAtMax?: Date }) => {
    return this.client.get<any>('/v1/claims/store/counts', {
      params: {
        ...params,
      },
    });
  };

  private searchClaims = (params?: { filters?: string }) => {
    return this.client.get<any>(`/v1/claims/search/${params?.filters}`);
  };

  private claimsForStore = (params?: {
    filters?: string;
    page?: number;
    limit?: number;
    createdAtMin?: Date;
    createdAtMax?: Date;
  }) => {
    return this.client.get<any>(`/v1/claims/list/store`, {
      params: {
        ...params,
      },
    });
  };

  private getClaimNavigation = (claimId: string, claimState: string) => {
    return this.client.get(`/v1/claims/${claimId}/navigation`, {
      params: { claimState },
    });
  };

  private claimGalleryCountsForStore = (claimTypes: ClaimType[], params?: { filters?: string }) => {
    const claimTypesString = claimTypes.join(',');

    return this.client.get<IssueGalleryCounts>(`/v1/claims/gallery/counts`, {
      params: { claimTypes: claimTypesString, ...params },
    });
  };

  private claimGalleryForStores = (
    claimTypes: ClaimType[],
    params?: {
      filters?: string;
      page?: number;
      limit?: number;
      createdAtMin?: Date;
      createdAtMax?: Date;
      sort?: string;
      order?: string;
    },
  ) => {
    const claimTypesString = claimTypes.join(',');

    return this.client.get<any>(`/v1/claims/gallery/list`, {
      params: { claimTypes: claimTypesString, ...params },
    });
  };

  private getClaimCreditUsage = (orderId: string) => {
    return this.client.get(`/v1/claims/credits/usages/${orderId}`);
  };

  private getResolvedCount = (storeId: string) => {
    return this.client.get(`/v1/claims/${storeId}/claim-resolutions/resolved-count`);
  };

  private getReviews = (
    storeId: string,
    params?: { page?: number; limit?: number; sort?: 'asc' | 'desc'; filters?: string },
  ) => {
    return this.client.get(`/v1/claims/reviews/store/${storeId}`, { params });
  };

  private getReviewsCount = (storeId: string, params?: { createdAtMin?: Date; createdAtMax?: Date }) => {
    return this.client.get<Record<number | string, number>>(`/v1/claims/reviews/store/${storeId}/count`, { params });
  };

  private setReviewChecked = (reviewId: string, checked: boolean) => {
    return this.client.patch(`/v1/claims/reviews/${reviewId}/check`, { checked });
  };

  private getReviewNotes = (reviewId: string) => {
    return this.client.get<ReviewNoteResponse[]>(`/v1/claims/reviews/${reviewId}/notes`);
  };

  private createReviewNote = (reviewId: string, body: CreateReviewNoteInput) => {
    return this.client.post<ReviewNoteResponse>(`/v1/claims/reviews/${reviewId}/notes`, body);
  };

  private deleteReviewNote = (reviewId: string, noteId: string) => {
    return this.client.delete(`/v1/claims/reviews/${reviewId}/notes/${noteId}`);
  };

  private getReviewsSummary = (
    storeId: string,
    params?: { createdAtMin?: Date; createdAtMax?: Date; filters?: string },
  ) => {
    return this.client.get<ReviewSummary>(`/v1/claims/reviews/store/${storeId}/summary`, { params });
  };

  private exportReviews = (storeId: string, body: { filters?: string; timezone?: string }) => {
    return this.client.post(`/v1/claims/reviews/store/${storeId}/export`, body);
  };

  private getWarranties = (
    storeIds: string[],
    params?: { filters?: string; page?: number; limit?: number; sort?: string; order?: string; automation?: string },
  ) => {
    return this.client.get(`/v1/claims/warranties/list`, {
      params,
    });
  };

  private getWarrantyCountsForStores = (storeIds: string[], params?: { filters?: string; automation?: string }) => {
    return this.client.get(`/v1/claims/warranties/counts`, {
      params,
    });
  };

  private exportWarranties = (body: { timezone: string; storeIds: string[]; filters: string }) => {
    return this.client.post<any>(`/v1/claims/warranties/export`, body);
  };

  private validateOffline = (claimId: string, body: ValidateOfflineDto) => {
    return this.client.post(`/v1/claims/warranties/validate/${claimId}`, body);
  };

  private getProductWarranty = (productId: string) => {
    return this.client.get(`/v1/claims/warranties/product/${productId}`);
  };

  private getShopifyActivities = (orderId: string, storeId: string) => {
    return this.client.get(`/v1/activities/shopify`, {
      params: {
        orderId,
        storeId,
      },
    });
  };

  // Every resolve and approve path goes through here, so this is where a resolved claim is announced to the seasonal
  // themes (the Thanksgiving turkey cheers). A refused request comes back with an `error` and is not announced.
  private resolveClaim = (claimId: string, body: ResolveClaimDto) => {
    return this.client.post(`/v1/claims/resolve/${claimId}`, body).pipe(
      tap(response => {
        if (response && !('error' in response)) useSeasonalCelebrationsStore().claimResolved();
      }),
    );
  };

  private resetClaim = (claimId: string, body: { sendEmail?: boolean }) => {
    return this.client.post(`/v1/claims/${claimId}/reset`, body);
  };

  private holdForBrand = (claimId: string, body: { question: string; media?: { fileKey: string }[] }) => {
    return this.client.post<Claim>(`/v1/claims/${claimId}/hold-for-brand`, body);
  };

  private updateClaimType = (
    claimId: string,
    body: { claimType: ClaimType; creditStore: boolean; overrideCreditStore?: boolean },
  ) => {
    return this.client.put(`/v1/claims/${claimId}/claim-type`, body);
  };

  private updateClaimState = (claimId: string, body: Record<string, any>) => {
    return this.client.put(`/v1/claims/${claimId}/state`, body);
  };

  private assignClaim = (claimId: string, assignedToUserId: string | null) => {
    return this.client.patch(`/v1/claims/${claimId}/assignment`, { assignedToUserId });
  };

  private createClaimMessage = (body: Record<string, any>) => {
    return this.client.post(`/v1/claims/message`, body);
  };

  private getClaimSubtypes = ({
    claimType,
    claimCategory,
  }: {
    claimType?: ClaimType;
    claimCategory?: ClaimCategory;
  }) => {
    const params: Record<string, any> = {};
    if (claimType !== undefined) params.claimType = claimType;
    if (claimCategory !== undefined) params.claimCategory = claimCategory;

    return this.client.get<ClaimSubtype[]>(`/v1/claims/config/subtypes`, { params });
  };

  private createClaimSubtype = (body: { name: string; parentType: ClaimType; category: ClaimCategory }) => {
    return this.client.post(`/v1/claims/config/subtypes`, body);
  };

  private updateClaimSubtype = (id: string, body: { name: string; shouldVoid: boolean }) => {
    return this.client.put(`/v1/claims/config/subtypes/${id}`, body);
  };

  private getAutomationById = (automationId: string) => {
    return this.client.get<EnrichedClaimAutomation>(`/v1/claims/automation/${automationId}`);
  };

  private listAutomationsByClaim = (claimId: string) => {
    return this.client.get<{
      claim: {
        id: string;
        storeId: string;
        orderId: string | null;
        sourceOrderNumber: string | null;
        claimType: string | null;
        claimState: string | null;
        createdAt: string;
      } | null;
      automations: EnrichedClaimAutomation[];
    }>(`/v1/claims/${claimId}/automations`);
  };

  private getCourseCorrectionStats = (params?: { startDate?: Date; endDate?: Date }) => {
    return this.client.get<{
      corrected: number;
      notCorrected: number;
      pending: number;
      total: number;
      rate: number | null;
    }>('/v1/claims/automation/course-correction-stats', { params });
  };

  private listAutomationsAdmin = (params?: {
    page?: number;
    limit?: number;
    filters?: string;
    hasFeedback?: boolean;
    includeResolutions?: boolean;
  }) => {
    return this.client.get<{ items: ClaimAutomation[]; total: number }>('/v1/claims/automation/list', {
      params: {
        filters: params?.filters,
        hasFeedback: params?.hasFeedback,
        includeResolutions: params?.includeResolutions,
        limit: params?.limit ?? 25,
        page: params?.page ?? 0,
      },
    });
  };

  private listClaimsWithAutomations = (params?: { page?: number; limit?: number; filters?: string }) => {
    return this.client.get<{
      items: {
        claimId: string;
        automationCount: number;
        latest: EnrichedClaimAutomation;
      }[];
      total: number;
    }>('/v1/claims/automation/claims-list', {
      params: {
        filters: params?.filters,
        limit: params?.limit ?? 25,
        page: params?.page ?? 0,
      },
    });
  };

  private getAutomationStats = (params?: { filters?: string }) => {
    return this.client.get<{
      total: number;
      totalEstimatedCost: number;
      avgRunSeconds: number | null;
      costByDate: { date: string; totalCost: number; claimCount: number }[];
      discardedFeedbackCount: number;
      resolverBreakdown: { ai: number; agent: number; autonomous: number };
      agentDecisions: { agentDecision: string | null; count: number }[];
      actions: { action: string; aiDecision: string | null; count: number }[];
      claimTypes: { claimType: string; count: number }[];
      volume: {
        createdAt: string;
        agentDecision: string | null;
        aiDecision: string | null;
        action: string | null;
        model: string | null;
        modelCompany: string | null;
        commitHash: string | null;
        claimType: string | null;
        hasDiscardedFeedback: boolean;
      }[];
    }>('/v1/claims/automation/stats', {
      params: { filters: params?.filters },
    });
  };

  /** The model a store's claim automation is set to, with its label and whether thinking changes it. */
  private getStoreModel = (storeId: string) => {
    return this.client.get<StoreModel>(`/v1/claims/automation/store-models/${storeId}`);
  };

  /** The models claim automation can call directly, by company/model id, for the store model picker. */
  private getDirectStoreModels = () => {
    return this.client.get<Array<{ label: string; value: string }>>('/v1/claims/automation/store-models/direct');
  };

  /** Sets the model a store's claim automation runs on, by its company/model id. */
  private setStoreModel = (storeId: string, input: { model: string }) => {
    return this.client.put<StoreModel>(`/v1/claims/automation/store-models/${storeId}`, input);
  };

  /** Each AI feature with the model and fallbacks it is set to; null fields while it runs its default. */
  private getFeatureModels = () => {
    return this.client.get<FeatureModel[]>('/v1/claims/automation/feature-models/list');
  };

  /** Sets the model an AI feature runs on, by its company/model id. */
  private setFeatureModel = (feature: string, input: { model: string }) => {
    return this.client.put<FeatureModel>(`/v1/claims/automation/feature-models/${feature}`, input);
  };

  /** Replaces an AI feature's fallbacks, in order, by company/model id, each optionally pinned to a provider; empty clears them. */
  private setFeatureFallbacks = (feature: string, input: { fallbacks: string[]; providers?: string[] }) => {
    return this.client.put<FeatureModel>(`/v1/claims/automation/feature-models/${feature}/fallbacks`, input);
  };

  /** The zero-retention providers, and the model's own host, a store on the model can be pinned to. */
  private getModelProviders = (aiModelId: string) => {
    return this.client.get<ModelProviders>(`/v1/claims/automation/model-providers/${aiModelId}`);
  };

  /** Moves every store on any row of the model onto its row for a provider; null onto the unpinned row. */
  private setModelProvider = (aiModelId: string, input: { provider: string | null }) => {
    return this.client.put<{ provider: string | null; stores: number }>(
      `/v1/claims/automation/model-providers/${aiModelId}`,
      input,
    );
  };

  /** Points one store at its model's ai_model row for a provider; null at the unpinned row. */
  private setStoreModelProvider = (storeId: string, input: { provider: string | null }) => {
    return this.client.put<{ provider: string | null; aiModelId: string }>(
      `/v1/claims/automation/store-models/${storeId}/provider`,
      input,
    );
  };

  /** Puts an AI feature back on its default model. */
  private resetFeatureModel = (feature: string) => {
    return this.client.delete<FeatureModel>(`/v1/claims/automation/feature-models/${feature}`);
  };

  private getGatewayModels = () => {
    return this.client.get<GatewayModel[]>('/v1/claims/automation/gateway-models/list');
  };

  private getEmbeddingModels = () => {
    return this.client.get<GatewayModel[]>('/v1/claims/automation/gateway-models/embeddings');
  };

  private getStoreAutomationStats = (params?: {
    filters?: string;
    sort?: StoreStatsSortKey;
    order?: 'asc' | 'desc';
    page?: number;
    limit?: number;
  }) => {
    return this.client.get<{
      items: StoreAutomationStatsItem[];
      total: number;
      startDate: string;
      endDate: string;
    }>('/v1/claims/automation/store-stats', {
      params: {
        filters: params?.filters,
        limit: params?.limit,
        order: params?.order,
        page: params?.page,
        sort: params?.sort,
      },
    });
  };

  private getModelAutomationStats = (params?: { startDate?: string; endDate?: string; filters?: string }) => {
    return this.client.get<{
      models: StoreModelSummary[];
      totals: ModelStatsTotals;
      startDate: string;
      endDate: string;
    }>('/v1/claims/automation/model-stats', {
      params: { endDate: params?.endDate, filters: params?.filters, startDate: params?.startDate },
    });
  };

  private getAiSpend = (params: {
    allStores?: boolean;
    startDate: string;
    endDate: string;
    featureFilters?: string;
    modelFilters?: string;
    storeFilters?: string;
    featureMetric?: string;
    modelMetric?: string;
    storeMetric?: string;
  }) => {
    return this.client.get<AiSpendResponse>('/v1/claims/automation/ai-spend/summary', { params });
  };

  private getModelTokensByDate = (model: string, params?: { startDate?: string; endDate?: string }) => {
    return this.client.get<{
      series: ModelTokenDay[];
      cohortSeries: ModelCohortDay[];
      startDate: string;
      endDate: string;
    }>(`/v1/claims/automation/model-stats/${encodeURIComponent(model)}/tokens-by-date`, {
      params: { endDate: params?.endDate, startDate: params?.startDate },
    });
  };

  private executeAutomation = (
    claimId: string,
    automationId: string,
    body: { skipMessage?: boolean; customerMessage?: string },
  ) => {
    return this.client.post(`/v1/claims/${claimId}/automation/${automationId}/execute`, body);
  };

  private listOpenRecommendations = (params?: { filters?: string }) => {
    return this.client.get<OpenRecommendationList>(`/v1/claims/automation/admin/control-panel/recommendations`, {
      params,
    });
  };

  private retryFailedAutomations = (automationIds: string[]) => {
    return this.client.post<{ retried: string[]; skipped: { automationId: string; reason: string }[] }>(
      `/v1/claims/automation/admin/control-panel/failed/retry`,
      { automationIds },
    );
  };

  private approveOpenRecommendations = (automationIds: string[]) => {
    return this.client.post<{ queued: string[]; skipped: string[] }>(
      `/v1/claims/automation/admin/control-panel/recommendations/approve`,
      { automationIds },
    );
  };

  private approveAutomationRecommendation = (claimId: string, automationId: string) => {
    return this.client.post(`/v1/claims/${claimId}/automation/${automationId}/approve-recommendation`, {});
  };

  private getClaimEscalation = (claimId: string) => {
    return this.client.get<ClaimEscalationPeek | null>(`/v1/claims/${claimId}/automation/escalation`);
  };

  private getClaimFraudCheck = (claimId: string) => {
    return this.client.get<ClaimFraudCheck | null>(`/v1/claims/${encodeURIComponent(claimId)}/fraud-check`, undefined, {
      noCache: true,
    });
  };

  private getActiveAutomation = (claimId: string) => {
    return this.client.get(`/v1/claims/${claimId}/automation/active`);
  };

  private getAutomationStatus = (claimId: string) => {
    return this.client.get(`/v1/claims/${claimId}/automation/status`);
  };

  private stopAutomation = (claimId: string) => {
    return this.client.post<{ claimId: string; stopped: boolean }>(`/v1/claims/${claimId}/automation/stop`);
  };

  private getAutomationPause = (claimId: string) => {
    return this.client.get<AutomationPauseRecord | null>(`/v1/claims/${claimId}/automation/pause`);
  };

  private extendAutomationPause = (claimId: string) => {
    return this.client.post<AutomationPauseRecord>(`/v1/claims/${claimId}/automation/pause/extend`);
  };

  private pauseAutomation = (
    claimId: string,
    body: {
      reasonCode: ClaimPauseReason;
      reason?: string | null;
      stoppedAutomationId?: string;
      stoppedScheduledExecutionAt?: string;
    },
  ) => {
    return this.client.post<AutomationPauseRecord>(`/v1/claims/${claimId}/automation/pause`, body);
  };

  private resumeAutomation = (claimId: string) => {
    return this.client.delete(`/v1/claims/${claimId}/automation/pause`);
  };

  private regenerateAutomation = (claimId: string, body?: { manualOnly?: boolean }) => {
    return this.client.post<{ claimId: string }>(`/v1/claims/${claimId}/automation/request`, body);
  };

  private submitAutomationFeedback = (
    claimId: string,
    automationId: string,
    body: { feedbackType: string; message: string | null; storeSpecific?: boolean },
  ) => {
    return this.client.post(`/v1/claims/${claimId}/automation/${automationId}/feedback`, body);
  };

  private updateAutomation = (
    claimId: string,
    automationId: string,
    body: { isActive?: boolean; agentDecision?: AgentDecision },
  ) => {
    return this.client.put(`/v1/claims/${claimId}/automation/${automationId}`, body);
  };

  private submitAutomationGuidance = (claimId: string, automationId: string, body: { guidance: string }) => {
    return this.client.post<{ claimId: string; status: string }>(
      `/v1/claims/${claimId}/automation/${automationId}/guidance`,
      body,
    );
  };

  private getBrandVoice = (storeSlug: string) => {
    return this.client.get<{ soul: string; agent: string }>(`/v1/claims/automation/brand-voice/${storeSlug}`);
  };

  private cancelScheduledExecution = (claimId: string, automationId: string) => {
    return this.client.post<{ automationId: string; status: string }>(
      `/v1/claims/${claimId}/automation/${automationId}/cancel-scheduled`,
    );
  };

  private listAutomationFeedback = (params?: {
    page?: number;
    limit?: number;
    filters?: string;
    includeResolutions?: boolean;
  }) => {
    return this.client.get<{ items: ClaimAutomationFeedback[] }>('/v1/claims/automation/feedback', {
      params: {
        filters: params?.filters,
        includeResolutions: params?.includeResolutions,
        limit: params?.limit ?? 25,
        page: params?.page ?? 0,
      },
    });
  };

  private countAutomationFeedback = (params?: { filters?: string }) =>
    this.client.get<number>('/v1/claims/automation/feedback/count', { params });

  private listFeedbackIssues = (params?: { filters?: string }) =>
    this.client.get<FeedbackIssuesResponse>('/v1/claims/automation/feedback/issues', { params });

  private updateFeedbackIssue = (body: UpdateTrainingIssueInput) =>
    this.client.put<UpdateTrainingIssueResult>('/v1/claims/automation/feedback/issues', body);

  private clusterFeedback = (body: { automationIds: string[] }) =>
    this.client.post<{ jobId: string }>('/v1/claims/automation/admin/train/cluster', body);

  private clusterFeedbackStatus = (jobId: string) =>
    this.client.get<{ state: string; result: { themes: FeedbackTheme[] } | null; failedReason: string | null }>(
      `/v1/claims/automation/admin/train/cluster/${jobId}`,
    );

  private discardFeedback = (body: { automationIds: string[] }) =>
    this.client.post<{ count: number }>('/v1/claims/automation/admin/train/discard', body);

  private discardFeedbackItems = (body: { feedbackIds: string[] }) =>
    this.client.post<{ count: number }>('/v1/claims/automation/admin/train/discard-feedback-items', body);

  private applyFeedback = (body: { automationIds: string[] }) =>
    this.client.post<{ count: number }>('/v1/claims/automation/admin/train/apply', body);

  private applyFeedbackItems = (body: { feedbackIds: string[] }) =>
    this.client.post<{ count: number }>('/v1/claims/automation/admin/train/apply-feedback-items', body);

  private auditFeedback = (body: { automationIds: string[] }) =>
    this.client.post<{ count: number }>('/v1/claims/automation/admin/train/audit', body);

  private auditFeedbackItems = (body: { feedbackIds: string[] }) =>
    this.client.post<{ count: number }>('/v1/claims/automation/admin/train/audit-feedback-items', body);

  private deleteFeedback = (body: { automationIds: string[] }) =>
    this.client.post<{ count: number }>('/v1/claims/automation/admin/train/delete', body);

  private deleteFeedbackItems = (body: { feedbackIds: string[] }) =>
    this.client.post<{ count: number }>('/v1/claims/automation/admin/train/delete-feedback-items', body);

  private generateTraining = (body: { themes: FeedbackTheme[] }) =>
    this.client.post<{ jobId: string }>('/v1/claims/automation/admin/train', body);

  private generateTrainingStatus = (jobId: string) =>
    this.client.get<{
      state: string;
      result: {
        suggestions: { file: string; rationale: string; originalContent: string; proposedContent: string }[];
        errors: string[];
      } | null;
      failedReason: string | null;
    }>(`/v1/claims/automation/admin/train/${jobId}`);

  private createTrainingPr = (body: {
    approvedChanges: { file: string; content: string; rationale: string }[];
    automationIds: string[];
  }) => this.client.post<{ prUrl: string }>('/v1/claims/automation/admin/train/pr', body);

  private getFailurePatterns = (params?: FailurePatternParams) => {
    return this.client.get<{
      piles: FailurePattern[];
      stores: StoreFailureRow[];
      totalFindings: number;
      totalUsd: number;
      totalDeliveries: number;
      failureRate: number | null;
    }>('/v1/claims/automation/patterns', { params });
  };

  private getFailurePatternVersions = (params?: FailurePatternParams) => {
    return this.client.get<PatternVersionBoundary[]>('/v1/claims/automation/patterns/versions', { params });
  };

  private listClaimSentiment = (params: ClaimSentimentListParams = {}) =>
    this.client.get<{ items: ClaimSentimentRow[]; total: number }>('/v1/claims/automation/sentiment', { params });

  private getClaimSentimentSummary = (params?: { startDate?: string; endDate?: string; filters?: string }) =>
    this.client.get<ClaimSentimentSummary>('/v1/claims/automation/sentiment/summary', { params });

  private getFailurePatternsByDate = (params?: FailurePatternParams) => {
    return this.client.get<FailurePatternDay[]>('/v1/claims/automation/patterns/by-date', { params });
  };

  private messageFeedbackAuthor = (feedbackId: string, body: { note?: string | null } = {}) =>
    this.client.post<{ channelId: string; messageTs?: string }>(
      `/v1/claims/automation/feedback/${feedbackId}/message-author`,
      body,
    );
}
